import type { SessionSettings } from '@cometchat/calls-sdk-javascript';
import { auth } from '../firebase';

const isE2ETest = import.meta.env.VITE_E2E_TEST === 'true';

/**
 * A CometChat employee account: signed in with Google on an @cometchat.com address.
 * Recording, captions and transcripts are staff-only on every host. One decision for the
 * call controls and Profile → Features.
 */
export function isCometChatAccount(): boolean {
  const user = auth.currentUser;
  return !!user && !user.isAnonymous && !!user.email?.toLowerCase().endsWith('@cometchat.com');
}

/**
 * The full settings a call is configured with: what the lobby (or the call
 * screen) chose plus the sample app's own defaults and overrides.
 *
 * Shared by meetings (Home) and 1:1 ringing calls (calls/CallProvider), so a
 * call gets the same chat button, virtual backgrounds, config overlay and
 * staff gating as a meeting.
 */
export function composeSessionSettings(
  lobbySettings: SessionSettings,
): SessionSettings {
  const staff = isCometChatAccount();
  return isE2ETest
    ? {
        // The call's own settings first (sessionType: a voice call stays voice), then the
        // e2e config overlay, which the tests drive. Returning the overlay alone made every
        // e2e-build call a video call with the camera on.
        ...lobbySettings,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ...(window as any).configState,
      }
    : {
        ...lobbySettings,
        title: lobbySettings.title || 'CometChat Team Meeting',
        hideChatButton: false,
        hideShareInviteButton: false,
        virtualBackground: {
          enableV2: true,
          // Custom virtual background images, replacing the 6
          // built-in CometChat images (leave `images` out to keep
          // them). Import `DEFAULT_VIRTUAL_BACKGROUND_IMAGES` from
          // `@cometchat/calls-sdk-javascript` to add to the defaults
          // instead of replacing them, and use `allowUserImages` /
          // `allowBlur` to hide the upload or blur options.
          // images: [
          //   'https://cdn.acme.com/backgrounds/office.jpg',
          //   { src: 'https://cdn.acme.com/backgrounds/beach.jpg', label: 'Beach' },
          // ],
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ...(window as any).configState,
        // Staff always get the recording and transcription buttons, whatever the
        // config overlay (which hides both by default) says.
        ...(staff
          ? { hideRecordingButton: false, hideTranscriptionButton: false }
          : {
              hideRecordingButton: true,
              hideClosedCaptionButton: true,
              hideTranscriptionButton: true,
            }),
      };
}
